import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import React, { useCallback, useMemo } from "react";
import { CRMChannel, TwilioMessageModel } from "@jugl-web/rest-api";
import { ChatMessageBubbleContextValue } from "@web-src/modules/chats/components/CommonMessageBubble/providers/ChatMessageBubbleProvider/ChatMessageBubbleContext";
import { ChatMessageBubbleAction } from "@web-src/modules/chats/components/CommonMessageBubble/types/ChatMessageBubbleAction";
import { CHAT_MESSAGE_PAYLOAD_VERSION } from "@jugl-web/utils/consts";
import { CommonMessageBubble } from "@web-src/modules/chats/components/CommonMessageBubble";
import { ChatMessageReplyInfo } from "@web-src/modules/chats/components/CommonMessageBubble/types/ChatMessageReplyInfo";
import { useAppVariant } from "@jugl-web/utils";
import { tokenizeMessageBody } from "@web-src/modules/chats/utils";
import { useGetSenderDetails } from "../../hooks/useGetSenderDetails";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";

export type TwilioMessageBubbleProps = {
  message: TwilioMessageModel;
  readOnly?: boolean;
  isSelectable?: boolean;
  isSelected?: boolean;
  isHighlighted?: boolean;
  isPreview?: boolean;
  onSelect?: (messageId: string, isSelected: boolean) => void;
  onTriggerAction?: (
    action: ChatMessageBubbleAction,
    message: TwilioMessageModel
  ) => void;
  onReplyPreviewClick?: (replyInfo: ChatMessageReplyInfo) => void;
  onReaction?: (reaction: string) => void;
  isFirstUnread?: boolean;
  isMessageActionsDisabled?: boolean;
  groupInfo?: {
    isFirst?: boolean;
    isLast?: boolean;
  };
};

export const TwilioMessageBubble: React.FC<TwilioMessageBubbleProps> = ({
  message,
  readOnly,
  isSelectable,
  isSelected,
  onSelect,
  onTriggerAction,
  onReplyPreviewClick,
  groupInfo,
  isHighlighted,
  isPreview,
  isMessageActionsDisabled,
}) => {
  const { entityId } = useEntitySelectedProvider();
  const { getSenderDetails } = useGetSenderDetails();
  const { setOpenedUserProfile } = useClientSupportPageContext();
  const { isCx } = useAppVariant();

  // Get message text body from payload
  const messageTextBody = useMemo(() => {
    return message.payload.text || "";
  }, [message]);

  const previewUrl = useMemo(() => {
    if (!messageTextBody) return undefined;
    return tokenizeMessageBody(messageTextBody).find(
      (item) => item.type === "url"
    )?.rawString;
  }, [messageTextBody]);

  const handleTriggerAction = useCallback(
    (action: ChatMessageBubbleAction) => {
      onTriggerAction?.(action, message);
    },
    [message, onTriggerAction]
  );

  const handleAvatarClick = useCallback(
    (userId: string) => {
      setOpenedUserProfile(userId);
    },
    [setOpenedUserProfile]
  );

  const isOutgoing = message.type === "outgoing";

  const chatMessageBubbleProps: ChatMessageBubbleContextValue = useMemo(() => {
    const availableActions = [];

    if (messageTextBody) {
      availableActions.push(ChatMessageBubbleAction.COPY);
    }

    return {
      messageId: message.msg_id,
      isDeleted: false,
      isUnsupported: false,
      isForwarded: false,
      messageClientId: message.id,
      messageFrom: message.from,
      isOutgoing,
      isGroupChat: true,
      showOutgoingSenderDetails: true,
      readOnly: !!readOnly,
      entityId,
      messageTextBody,
      isMediaMessageOnly: false,
      messagePayloadVersion: CHAT_MESSAGE_PAYLOAD_VERSION,
      isSelectable,
      isSelected,
      isMessageActionsDisabled,
      onReplyPreviewClick,
      onSelect,
      groupInfo,
      isHighlighted,
      isPreview,
      isEdited: false,
      outgoingStatus: isOutgoing ? (message.read ? "read" : "sent") : undefined,
      timestamp: message.payload.timestamp,
      availableActions,
      previewUrl,
      onAvatarClick: !isCx ? handleAvatarClick : undefined,
      getAttachmentUrls: () => ({
        preview: "",
        stream: "",
      }),
      triggerAction: handleTriggerAction,
      triggerReaction: () => {},
      isReactionDetailsDisabled: true,
      ...getSenderDetails(message, CRMChannel.twilio),
    };
  }, [
    messageTextBody,
    message,
    isOutgoing,
    readOnly,
    entityId,
    isSelectable,
    isSelected,
    isMessageActionsDisabled,
    onReplyPreviewClick,
    onSelect,
    groupInfo,
    isHighlighted,
    isPreview,
    previewUrl,
    isCx,
    handleAvatarClick,
    handleTriggerAction,
    getSenderDetails,
  ]);

  return <CommonMessageBubble {...chatMessageBubbleProps} />;
};
